import { useReducer } from 'react';
import { BASE_PRECISION_EXP, BigNum } from '@drift-labs/sdk';
import { roundToStepSizeIfLargeEnough } from '@drift/common';
import { BracketOrders } from 'src/components/TradeForm/TpSlOrderForm/types';

export type TpSlOrderFormV2State = {
	sliderPercentage: number;
	partialPositionSelected: boolean;
	positionEntrySize: BigNum;
	userInputEntrySize: BigNum;
	userInputEntrySizeString: string;
	userInputEntryQuoteSizeString?: string;
	bracketOrders: BracketOrders;
	stepSize: number;
	cancellingOrder: 'tp' | 'sl' | undefined;
};

export type TpSlOrderFormV2Action = {
	type: string;
	value: {
		size?: string;
		markPrice?: BigNum;
		partialPositionSelected?: boolean;
		sliderPercentage?: number;
		bracketOrders?: BracketOrders;
		cancellingOrder?: 'tp' | 'sl';
	};
};

const reducer = (
	state: TpSlOrderFormV2State,
	action: TpSlOrderFormV2Action
): TpSlOrderFormV2State => {
	switch (action.type) {
		case 'setPartialPositionSelected': {
			// If the checkbox is checked, we use the current previously set slider percentage
			// to calculate the user input entry size, otherwise we reset it to the full position
			const userInputEntrySize = action.value.partialPositionSelected
				? state.positionEntrySize.mul(new BigNum(state.sliderPercentage, 2))
				: state.positionEntrySize;
			return {
				...state,
				partialPositionSelected: action.value.partialPositionSelected ?? false,
				userInputEntrySize,
				userInputEntrySizeString: userInputEntrySize.toNum().toString(),
			};
		}
		case 'setSliderPercentage': {
			const sliderPercentage = state.partialPositionSelected
				? typeof action.value.sliderPercentage === 'number'
					? action.value.sliderPercentage
					: 0
				: 100;
			const multiplier = new BigNum(sliderPercentage, 2);
			const userInputEntrySize = state.positionEntrySize.mul(multiplier);
			const userInputEntrySizeString = roundToStepSizeIfLargeEnough(
				userInputEntrySize.toNum().toString(),
				state.stepSize
			);
			const userInputEntryQuoteSizeString = roundToStepSizeIfLargeEnough(
				userInputEntrySize
					.mul(action.value.markPrice ?? new BigNum(0))
					.toNum()
					.toString(),
				state.stepSize
			);
			return {
				...state,
				sliderPercentage,
				userInputEntrySize,
				userInputEntrySizeString,
				userInputEntryQuoteSizeString,
				partialPositionSelected: state.partialPositionSelected,
			};
		}
		case 'setUserInputEntryBaseSize': {
			let userInputEntrySizeString: string = action.value.size ?? '';
			let sliderPercentage: number;
			const userInputEntrySizeBN = BigNum.fromPrint(
				action.value.size ?? '',
				BASE_PRECISION_EXP
			);
			if (userInputEntrySizeBN.gt(state.positionEntrySize)) {
				userInputEntrySizeString = roundToStepSizeIfLargeEnough(
					state.positionEntrySize.toNum().toString(),
					state.stepSize
				);
				sliderPercentage = 100;
			} else {
				sliderPercentage =
					(userInputEntrySizeBN.toNum() / state.positionEntrySize.toNum()) *
					100;
			}
			return {
				...state,
				userInputEntrySize: userInputEntrySizeBN,
				userInputEntrySizeString,
				sliderPercentage,
				partialPositionSelected: state.partialPositionSelected,
			};
		}
		case 'setUserInputEntryQuoteSize': {
			let userInputEntryQuoteSizeString = action.value.size || '0';
			if (!action.value.markPrice) return state;

			const markPriceNum = action.value.markPrice.toNum();
			// Prevent division by zero
			if (markPriceNum === 0) {
				return state;
			}
			let userInputEntryBaseSizeString = (
				parseFloat(userInputEntryQuoteSizeString) / markPriceNum
			).toString();

			const userInputEntrySizeBN = BigNum.fromPrint(
				userInputEntryBaseSizeString,
				BASE_PRECISION_EXP
			);
			let sliderPercentage: number;
			if (userInputEntrySizeBN.gt(state.positionEntrySize)) {
				userInputEntryBaseSizeString = roundToStepSizeIfLargeEnough(
					state.positionEntrySize.toNum().toString(),
					state.stepSize
				);
				userInputEntryQuoteSizeString = state.positionEntrySize
					.mul(action.value.markPrice)
					.toNum()
					.toString();
				sliderPercentage = 100;
			} else {
				sliderPercentage =
					(userInputEntrySizeBN.toNum() / state.positionEntrySize.toNum()) *
					100;
			}
			return {
				...state,
				userInputEntrySize: userInputEntrySizeBN,
				sliderPercentage,
				userInputEntrySizeString: userInputEntryBaseSizeString,
				userInputEntryQuoteSizeString,
				partialPositionSelected: state.partialPositionSelected,
			};
		}

		case 'setBracketOrders': {
			return {
				...state,
				bracketOrders: {
					...action.value.bracketOrders,
				},
			};
		}
		case 'cancellingOrder': {
			return {
				...state,
				cancellingOrder: action.value.cancellingOrder,
			};
		}
		default:
			return state;
	}
};

export const useTpSlOrderFormReducer = (
	maxBaseSizeString: string,
	stepSize: number,
	markPrice: BigNum
) => {
	const maxBaseSizeBigNum = BigNum.fromPrint(
		maxBaseSizeString,
		BASE_PRECISION_EXP
	);

	const [state, dispatch] = useReducer(reducer, {
		sliderPercentage: 100,
		partialPositionSelected: false,
		positionEntrySize: maxBaseSizeBigNum,
		userInputEntrySize: maxBaseSizeBigNum,
		userInputEntrySizeString: maxBaseSizeString,
		userInputEntryQuoteSizeString: roundToStepSizeIfLargeEnough(
			maxBaseSizeBigNum.mul(markPrice).toNum().toString(),
			stepSize
		),
		bracketOrders: {
			takeProfit: undefined,
			stopLoss: undefined,
		},
		stepSize,
		cancellingOrder: undefined,
	});

	return [state, dispatch] as const;
};
